import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import set from "lodash/set";
import {
    CMP_CONTACT_CARDS, CmpContactCard, CmpContactCardId, cmpPendingGrantPath, getCardFields, getPrimaryContactFields, hasPendingGrant, isSameAsPrimary,
} from "../CmpContacts/cmpContactCards";
import { getCmpPhoneDigits } from "./cmpTollFreeFax";

// Claude Design "CMP Accelerator Member Dashboard" (Oct 16 2026), Manage
// Company Profile: every card saves on its own (Reset, Save Changes, Last
// Saved). Section ids are the design data-section ids (D1). Texts are the
// design text verbatim (D15-D18).

export type CmpProfileSectionId = "profile" | "address" | "social" | "primary" | "ap" | "admin" | "mktg" | "hr" | "sales" | "featured";

export const CMP_PROFILE_SECTION_IDS: CmpProfileSectionId[] = ["profile", "address", "social", "primary", "ap", "admin", "mktg", "hr", "sales", "featured"];

/** D16 card names (design secLabels). */
export const CMP_PROFILE_SECTION_NAMES: Record<CmpProfileSectionId, string> = {
    profile: "Company Profile",
    address: "Company Address",
    social: "Links to Social Media",
    primary: "Company Primary Contact",
    ap: "Accounts Payable Contact",
    admin: "Company Administrator Contact",
    mktg: "Marketing & Communications Contact",
    hr: "HR/Employee Engagement Contact",
    sales: "Sales/Business Development Contact",
    featured: "Featured Member Banners",
};

/** The contact card of each contact section. */
export const CMP_CONTACT_SECTION: Record<CmpContactCardId, CmpProfileSectionId> = {
    accountsPayable: "ap", administrator: "admin", marketingCommunication: "mktg", hrEmployeeEngagement: "hr", sales: "sales",
};

/** Design bottom Update popup: short contact names in front of each contact row. */
const UPDATE_CONTACT_PREFIX: Partial<Record<CmpProfileSectionId, string>> = {
    ap: "Accounts Payable Contact", admin: "Company Administrator Contact", mktg: "Marketing Contact", hr: "HR Contact", sales: "Sales Contact",
};

export const CMP_PROFILE_RESULT_TEXT = {
    missingTitle: "MISSING REQUIRED FIELDS",
    missingIntro: "Fill out the following required fields and save again.",
    invalidTitle: "INVALID EMAIL ADDRESS",
    invalidIntro: "The email address entered is not valid. Correct it and save again.",
    mixedTitle: "PLEASE FIX THE FOLLOWING",
    mixedIntro: "Some fields are missing or not valid. Correct them and save again.",
    savedTitle: "CHANGES SAVED",
    updatedTitle: "PROFILE UPDATED",
    updatedMessage: "Your company profile has been updated successfully.",
    required: "This field is required.",
    nameAndEmailToGrant: "Name and email need to be filled to grant access.",
    nameAndEmail: "Name and email need to be filled.",
    nameToGrant: "Name is required to grant access.",
    emailToGrant: "Email is required to grant access.",
    contactInvalidEmail: "Invalid email address. Use the format name@company.com.",
};

export const cmpSectionSavedMessage = (section: CmpProfileSectionId) => `${CMP_PROFILE_SECTION_NAMES[section]} has been saved successfully.`;
const invalidEmailWhy = (value: string) => `"${value}" is not a valid email address. Use the format name@company.com.`;

const contactCard = (section: CmpProfileSectionId): CmpContactCard | undefined =>
    CMP_CONTACT_CARDS.find((card) => CMP_CONTACT_SECTION[card.contactId] === section);

/** Form value paths of each card. The page keeps the Featured Member banners in "featuredBanners". */
export const CMP_PROFILE_SECTION_FIELDS: Record<CmpProfileSectionId, string[]> = (() => {
    const contact = (section: CmpProfileSectionId) => {
        const card = contactCard(section) as CmpContactCard;
        return [...Object.values(card.fieldNames), cmpPendingGrantPath(card.contactId),
            ...(card.billingAddress ? ["streetAddress", "city", "province", "postalCode", "country"].map((key) => `cmpBillingAddress.${key}`) : [])];
    };
    return {
        profile: ["name", "website", "googleReviewLink", "contactName", "email", "phoneNumber", "phoneNumberExtension", "tollFreePhoneNumber", "faxNumber", "description", "logo", "categoryId"],
        address: ["address.streetAddress", "address.city", "address.province", "address.postalCode", "address.country", "hideAddressFromDirectory"],
        social: ["facebook", "instagram", "threads", "linkedin", "twitter", "tiktok", "youtube"],
        primary: ["primaryContactName", "primaryContactTitle", "primaryContactEmail", "primaryContactPhoneNumber",
            "primaryContactPhoneNumberExtension", "primaryContactMobilePhoneNumber"],
        ap: contact("ap"), admin: contact("admin"), mktg: contact("mktg"), hr: contact("hr"), sales: contact("sales"),
        featured: ["featuredBanners"],
    };
})();

export type CmpProfileIssue = {
    field: string; kind: "missing" | "invalid"; why: string;
    /** Form fields to mark red and focus after the pop-up closes (MD-N6). */
    paths?: string[];
};
/** Design categories field id (data-field="categories", ACC:207). */
export const CMP_CATEGORIES_FIELD = "categoryId";
export type CmpSectionContext = {
    /** True when the contact has Member Dashboard access saved on the backend. */
    isEnabled: (contactId: CmpContactCardId) => boolean;
};

const text = (values: unknown, path: string): string => {
    const value = get(values, path);
    return typeof value === "string" ? value : "";
};
// Design email rule (scanMissing / contactIssues).
const isDesignEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);

/** Design contactIssues: name and email when access is granted (AP also when any field is filled), and the email format. */
const contactIssues = (card: CmpContactCard, values: unknown, context: CmpSectionContext): CmpProfileIssue[] => {
    const fields = getCardFields(values, card);
    if (isSameAsPrimary(fields, getPrimaryContactFields(values))) return [];
    const granted = context.isEnabled(card.contactId) || hasPendingGrant(values, card.contactId);
    const name = fields.name.trim();
    const email = fields.email.trim();
    const anyFilled = Object.values(fields).some((value) => value.trim());
    const required = granted || (card.contactId === "accountsPayable" && anyFilled);
    const T = CMP_PROFILE_RESULT_TEXT;
    const issues: CmpProfileIssue[] = [];
    if (required && !name && !email) {
        issues.push({ field: "Contact Name & Contact Email", kind: "missing", why: granted ? T.nameAndEmailToGrant : T.nameAndEmail,
            paths: [card.fieldNames.name, card.fieldNames.email] });
    } else {
        if (required && !name) issues.push({ field: "Contact Name", kind: "missing", why: granted ? T.nameToGrant : T.required, paths: [card.fieldNames.name] });
        if (required && !email) issues.push({ field: "Contact Email", kind: "missing", why: granted ? T.emailToGrant : T.required, paths: [card.fieldNames.email] });
    }
    if (email && !isDesignEmail(email)) issues.push({ field: "Contact Email", kind: "invalid", why: T.contactInvalidEmail, paths: [card.fieldNames.email] });
    return issues;
};

const requiredIssue = (values: unknown, path: string, label: string): CmpProfileIssue[] =>
    text(values, path).trim() ? [] : [{ field: label, kind: "missing", why: CMP_PROFILE_RESULT_TEXT.required, paths: [path] }];
const emailIssue = (values: unknown, path: string, label: string, required: boolean): CmpProfileIssue[] => {
    const value = text(values, path).trim();
    if (!value) return required ? [{ field: label, kind: "missing", why: CMP_PROFILE_RESULT_TEXT.required, paths: [path] }] : [];
    return isDesignEmail(value) ? [] : [{ field: label, kind: "invalid", why: invalidEmailWhy(value), paths: [path] }];
};

/** D15: Save Changes checks only its own card (starred fields, email format, name and email to grant access). */
export const validateSection = (section: CmpProfileSectionId, values: unknown, context: CmpSectionContext, prefix?: string): CmpProfileIssue[] => {
    let issues: CmpProfileIssue[] = [];
    switch (section) {
        case "profile":
            issues = [
                ...requiredIssue(values, "name", "Company Name"),
                // Not starred in the design; an invalid address was refused before and is still refused.
                ...emailIssue(values, "email", "Main Contact Email", false),
                // IF11 MB4: optional, but a partial number is refused.
                ...(getCmpPhoneDigits(text(values, "tollFreePhoneNumber")).length % 11 ? [{ field: "Toll-Free Number", kind: "invalid" as const,
                    why: "Enter 11 digits, for example 1-800-555-0142, or leave it blank.", paths: ["tollFreePhoneNumber"] }] : []),
                ...(getCmpPhoneDigits(text(values, "faxNumber")).length % 10 ? [{ field: "Fax Number", kind: "invalid" as const,
                    why: "Enter 10 digits, for example (705) 288-6061, or leave it blank.", paths: ["faxNumber"] }] : []),
                ...(((get(values, "categoryId") as unknown[] | undefined) || []).length ? []
                    // MD-N1 (ACC:2622-2625): design row text.
                    : [{ field: "Website and Chamber Perks App™ Directory Categories", kind: "missing" as const, why: "Select 1 to 2 categories.",
                        paths: [CMP_CATEGORIES_FIELD] }]),
            ];
            break;
        case "address":
            issues = [
                ...requiredIssue(values, "address.streetAddress", "Street Address"),
                ...requiredIssue(values, "address.city", "City"),
                ...requiredIssue(values, "address.province", "Province/State"),
                ...requiredIssue(values, "address.postalCode", "Postal/Zip Code"),
                ...requiredIssue(values, "address.country", "Country"),
            ];
            break;
        case "primary":
            issues = [...requiredIssue(values, "primaryContactName", "Contact Name"), ...emailIssue(values, "primaryContactEmail", "Contact Email", true)];
            break;
        case "ap": case "admin": case "mktg": case "hr": case "sales":
            issues = contactIssues(contactCard(section) as CmpContactCard, values, context);
            break;
        default:
            issues = [];
    }
    return prefix ? issues.map((issue) => ({ ...issue, field: `${prefix} – ${issue.field}` })) : issues;
};

/**
 * Checks several cards at once. "update" (bottom Update, D18) puts the design
 * short contact names in front of contact rows; "leave" (Save & Update in the
 * unsaved changes popup, D19) puts the card name in front of every row.
 */
export const validateSections = (sections: CmpProfileSectionId[], values: unknown, context: CmpSectionContext, mode: "update" | "leave"): CmpProfileIssue[] =>
    sections.flatMap((section) => validateSection(section, values, context,
        mode === "leave" ? CMP_PROFILE_SECTION_NAMES[section] : UPDATE_CONTACT_PREFIX[section]));

/** Popup title and intro for the issues (D17). */
export const getIssuePopupHeading = (issues: CmpProfileIssue[]) => {
    const T = CMP_PROFILE_RESULT_TEXT;
    const invalid = issues.filter((issue) => issue.kind === "invalid").length;
    const missing = issues.length - invalid;
    if (invalid && !missing) return { title: T.invalidTitle, intro: T.invalidIntro };
    if (missing && !invalid) return { title: T.missingTitle, intro: T.missingIntro };
    return { title: T.mixedTitle, intro: T.mixedIntro };
};

const copyFields = (target: Record<string, unknown>, source: unknown, sections: CmpProfileSectionId[]) => {
    sections.forEach((section) => CMP_PROFILE_SECTION_FIELDS[section].forEach((path) => {
        set(target, path, cloneDeep(get(source, path)));
    }));
    return target;
};

/**
 * Values for a card save: the saved values of every other card plus the
 * current values of the saved card(s). Unsaved edits on other cards are
 * neither sent nor lost.
 */
export const buildSectionInput = <T extends Record<string, unknown>>(sections: CmpProfileSectionId | CmpProfileSectionId[], current: T, saved: T): T =>
    copyFields(cloneDeep(saved), current, Array.isArray(sections) ? sections : [sections]) as T;

/** The new saved snapshot after a save. A saved card has no grant tick waiting. */
export const markSectionsSaved = <T extends Record<string, unknown>>(sections: CmpProfileSectionId[], current: T, saved: T): T => {
    const next = buildSectionInput(sections, current, saved);
    CMP_CONTACT_CARDS.forEach((card) => {
        if (sections.includes(CMP_CONTACT_SECTION[card.contactId])) set(next, cmpPendingGrantPath(card.contactId), false);
    });
    return next;
};

/** D20 Reset: the card's last saved values; other cards keep their edits. */
export const resetSectionValues = <T extends Record<string, unknown>>(section: CmpProfileSectionId, current: T, saved: T): T =>
    copyFields(cloneDeep(current), saved, [section]) as T;

export const isSectionDirty = (section: CmpProfileSectionId, current: unknown, saved: unknown) =>
    CMP_PROFILE_SECTION_FIELDS[section].some((path) => !isEqual(get(current, path), get(saved, path)));

/** Last Saved times by section from the backend list. */
export const toSectionSavedAt = (rows: readonly { section: string; savedAtUtcMilli: number }[] | null | undefined) =>
    (rows || []).reduce<Partial<Record<CmpProfileSectionId, number>>>((result, row) => ({ ...result, [row.section]: row.savedAtUtcMilli }), {});
